import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, MessageSquareText, LineChart, Landmark,
  GraduationCap, Dumbbell, PiggyBank, Code2, Sparkles, Users, Smartphone,
  Repeat, Zap, CheckCircle2, Wallet, MessageCircle, Send, Mail, Clock,
} from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen">
      {/* Top bar */}
      <header className="co-glass-header sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/collectone-logo.webp" alt="CollectOne" className="w-9 h-9 object-contain" />
            <span className="font-heading font-bold tracking-tight text-lg">CollectOne</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link
              to="/login"
              data-testid="landing-login-link"
              className="text-sm font-semibold text-[#0A0A0B] hover:text-[#4338CA] transition-colors duration-200"
            >
              Log in
            </Link>
            <Link
              to="/signup"
              data-testid="landing-signup-link"
              className="inline-flex items-center gap-1.5 bg-[#0A0A0B] text-white px-4 py-2 text-sm font-semibold rounded-sm co-btn-press hover:bg-[#27272A]"
            >
              Free trial · 3 days
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </header>

      {/* Running marquee banner */}
      <div
        className="border-b co-hairline overflow-hidden bg-[#0A0A0B] text-white py-2.5"
        data-testid="landing-marquee"
        aria-label="Who CollectOne is for"
      >
        <div className="co-marquee whitespace-nowrap flex gap-16 text-sm font-semibold">
          {Array.from({ length: 4 }).map((_, i) => (
            <span key={i} className="inline-flex items-center gap-3">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#4338CA]" />
              Whether you&apos;re a financial consultant, MFD, insurance advisor, school, coaching institute, SaaS company, agency, gym, clinic, or small business, CollectOne is built for you.
            </span>
          ))}
        </div>
      </div>

      {/* Social proof strip */}
      <div
        className="border-b co-hairline bg-white"
        data-testid="landing-social-proof"
        aria-label="Trusted across industries"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 lg:py-6">
          <div className="flex items-center justify-between gap-6 flex-wrap">
            <div className="co-label text-[#52525B] shrink-0">Trusted across industries</div>
            <div className="flex items-center gap-6 sm:gap-10 flex-wrap">
              {[
                { Icon: Landmark, label: "LIC" },
                { Icon: PiggyBank, label: "HDFC MF" },
                { Icon: GraduationCap, label: "Coaching" },
                { Icon: Dumbbell, label: "Gym & Fitness" },
                { Icon: Code2, label: "SaaS" },
              ].map(({ Icon, label }) => (
                <div
                  key={label}
                  data-testid={`proof-logo-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="flex items-center gap-2 text-[#52525B] hover:text-[#0A0A0B] transition-colors duration-200"
                >
                  <Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
                  <span className="text-xs font-semibold tracking-wide">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden border-b co-hairline">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6 flex-wrap">
              <div className="co-label">B2B · Multi-user SaaS</div>
              <div
                data-testid="hero-trial-badge"
                className="inline-flex items-center gap-1.5 bg-[#F0FDF4] border border-[#BBF7D0] text-[#15803D] px-2.5 py-1 rounded-sm text-[11px] font-bold tracking-wide uppercase"
              >
                <Sparkles className="h-3 w-3" strokeWidth={2.25} />
                3-day free trial · no card
              </div>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]">
              Stop chasing payments.
              <br />
              <span className="text-[#4338CA]">Start growing your business.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#52525B] max-w-xl leading-relaxed">
              Automate subscription reminders across WhatsApp, Email and SMS.
              Manage recurring collections on autopilot — insurance premiums,
              SIPs, school fees, gym memberships and every other subscription.
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/signup"
                data-testid="hero-signup-button"
                className="inline-flex items-center gap-2 bg-[#4338CA] hover:bg-[#3730A3] text-white px-5 py-3 text-sm font-semibold rounded-sm co-btn-press"
              >
                Start 3-day free trial
                <ArrowRight className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 bg-white border border-[#0A0A0B] text-[#0A0A0B] px-5 py-3 text-sm font-semibold rounded-sm co-btn-press hover:bg-[#F4F4F5]"
              >
                Sign in
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6">
              {[
                { k: "Reminders / mo", v: "Unlimited" },
                { k: "Channels", v: "WA · Email · SMS" },
                { k: "Insurers · AMCs", v: "24+ pre-loaded" },
                { k: "Multi-user", v: "Team access" },
              ].map((s) => (
                <div key={s.k}>
                  <div className="co-label">{s.k}</div>
                  <div className="mt-1 font-heading text-lg font-bold tracking-tight">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative border co-hairline bg-white p-1 rounded-sm">
              <img
                src="https://customer-assets-jt897jd0.emergentagent.net/job_payment-remind-13/artifacts/5vvqtxdq_Collect%20One%20Hero%20Image..png"
                alt="CollectOne — One Platform. Every Collection."
                className="w-full h-auto object-contain rounded-sm"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Razorpay zero-commission callout — MAJOR differentiator */}
      <section className="border-b co-hairline bg-[#F0FDF4]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 lg:py-12 grid lg:grid-cols-[auto_1fr_auto] gap-6 lg:gap-10 items-center">
          <div className="w-14 h-14 bg-white border border-[#BBF7D0] grid place-items-center rounded-sm shrink-0">
            <Wallet className="h-7 w-7 text-[#15803D]" strokeWidth={1.75} />
          </div>
          <div>
            <div className="co-label text-[#166534] mb-2">Bring your own gateway</div>
            <h2 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight text-[#052E16]">
              Your Razorpay account. <span className="text-[#15803D]">Zero commission sharing.</span>
            </h2>
            <p className="mt-2 text-sm lg:text-base text-[#166534] leading-relaxed max-w-2xl">
              Every rupee your customer pays lands directly in <em>your</em> Razorpay account.
              We never touch the money and take no commission — unlike other platforms that skim
              1-3% off every transaction. Add your keys once, keep 100% of your collections forever.
            </p>
          </div>
          <div className="hidden lg:flex flex-col items-end text-right shrink-0">
            <div className="font-heading text-4xl font-bold tracking-tight text-[#15803D]">0%</div>
            <div className="text-xs font-semibold text-[#166534] uppercase tracking-wider">commission</div>
          </div>
        </div>
      </section>

      {/* Feature strip - Built for two audiences */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="co-label mb-8">Built for two audiences</div>
        <div className="grid grid-cols-1 md:grid-cols-2 border co-hairline">
          <div className="p-8 border-b md:border-b-0 md:border-r co-hairline">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#ECECFE] text-[#4338CA] grid place-items-center rounded-sm">
                <ShieldCheck className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
              </div>
              <h3 className="font-heading text-xl font-bold tracking-tight">
                Payment-collecting businesses
              </h3>
            </div>
            <p className="text-sm text-[#52525B] leading-relaxed">
              Coaching, schools, SaaS, gyms, subscription services. Plug in your own Razorpay
              for auto-tracked payments, a UPI ID you confirm manually, or any external checkout
              link. Every reminder shows your active options.
            </p>
          </div>
          <div className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#FEF3E7] text-[#B45309] grid place-items-center rounded-sm">
                <Landmark className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
              </div>
              <h3 className="font-heading text-xl font-bold tracking-tight">Financial distributors</h3>
            </div>
            <p className="text-sm text-[#52525B] leading-relaxed">
              Insurance agents &amp; MF distributors. Never handle a rupee yourself — pick from
              a pre-loaded directory of insurers and AMCs. Every reminder routes clients to the
              correct payment portal.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 border-l border-r border-b co-hairline">
          {[
            {
              icon: MessageSquareText,
              t: "Multi-channel reminders",
              d: "WhatsApp click-to-chat, WhatsApp Business, transactional Email and copy-and-send SMS templates — pick what fits your budget.",
            },
            {
              icon: LineChart,
              t: "Live collection dashboard",
              d: "Overdue, upcoming, pending amount and monthly collected — always visible at a glance. Export ageing reports in one click.",
            },
            {
              icon: Smartphone,
              t: "Mobile Friendly Interface",
              d: "Runs beautifully on your phone — chase, retag and mark paid on the go. No mobile app to install, works from any browser.",
            },
          ].map((f, i) => (
            <div
              key={f.t}
              className={`p-8 ${i < 2 ? "border-b md:border-b-0 md:border-r co-hairline" : ""}`}
            >
              <f.icon className="h-5 w-5 text-[#4338CA] mb-3" strokeWidth={1.75} aria-hidden="true" />
              <div className="font-heading text-lg font-bold tracking-tight mb-1">{f.t}</div>
              <div className="text-sm text-[#52525B] leading-relaxed">{f.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Subscriptions on autopilot — highlight recurring management */}
      <section className="border-t co-hairline bg-[#0A0A0B] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <div
                className="inline-flex items-center gap-1.5 bg-white/10 border border-white/15 text-white px-2.5 py-1 rounded-sm text-[11px] font-bold tracking-wide uppercase mb-5"
                data-testid="landing-subs-badge"
              >
                <Repeat className="h-3 w-3" strokeWidth={2.25} />
                Subscriptions on autopilot
              </div>
              <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tighter leading-[1.1]">
                Manage every recurring payment
                <br />
                <span className="text-[#A5B4FC]">with zero manual follow-up.</span>
              </h2>
              <p className="mt-5 text-sm lg:text-base text-white/70 leading-relaxed max-w-lg">
                Insurance premiums. SIPs. School fees. Coaching installments. Gym renewals.
                SaaS plans. Set the cycle once — CollectOne handles the rest, forever.
              </p>
              <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
                {[
                  "Monthly · Quarterly · Yearly",
                  "Auto-advance due dates",
                  "Renewal receipts on paid",
                  "Snooze &amp; retry logic",
                ].map((f) => (
                  <div key={f} className="flex items-start gap-2 text-sm text-white/85">
                    <CheckCircle2 className="h-4 w-4 text-[#A5B4FC] mt-0.5 shrink-0" strokeWidth={2} />
                    <span dangerouslySetInnerHTML={{ __html: f }} />
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: MessageCircle, t: "WhatsApp Reminder", color: "text-[#4ADE80]" },
                { icon: Mail, t: "Email Reminder", color: "text-[#A5B4FC]" },
                { icon: MessageSquareText, t: "SMS Reminder", color: "text-[#FBBF24]" },
                { icon: Send, t: "Payment Link", color: "text-[#F472B6]" },
                { icon: Zap, t: "UPI QR / eNACH", color: "text-[#22D3EE]" },
                { icon: Clock, t: "Thank-you Message", color: "text-[#FB923C]" },
              ].map((c) => (
                <div key={c.t} className="border border-white/10 bg-white/5 p-4 rounded-sm">
                  <c.icon className={`h-5 w-5 ${c.color} mb-2`} strokeWidth={1.75} />
                  <div className="text-sm font-semibold">{c.t}</div>
                  <div className="text-[11px] text-white/50 mt-0.5">Automatic</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp options — you pick what fits your budget */}
      <section className="border-t co-hairline">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <div className="co-label mb-3">WhatsApp — your choice</div>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tighter leading-[1.1] max-w-3xl">
            Use WhatsApp, WhatsApp Business, or Business API.
            <br />
            <span className="text-[#4338CA]">Whatever fits your budget.</span>
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-0 border co-hairline">
            {[
              {
                t: "WhatsApp (personal)",
                sub: "Free · Zero setup",
                d: "One-tap wa.me links open your own WhatsApp with the reminder pre-filled. Perfect to start today, no gateway or API needed.",
              },
              {
                t: "WhatsApp Business",
                sub: "Free · Your business profile",
                d: "Same one-tap flow but from your verified business number so clients see your logo, hours and away message.",
              },
              {
                t: "WhatsApp Business API",
                sub: "Paid · Auto-send at scale",
                d: "Bring your BSP credentials once. Reminders fire automatically at scheduled times — hands off, ideal at 500+ clients.",
              },
            ].map((o, i) => (
              <div
                key={o.t}
                className={`p-7 ${i < 2 ? "border-b md:border-b-0 md:border-r co-hairline" : ""}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <MessageCircle className="h-5 w-5 text-[#16A34A]" strokeWidth={1.75} />
                  <div className="font-heading text-lg font-bold tracking-tight">{o.t}</div>
                </div>
                <div className="text-[11px] font-semibold text-[#16A34A] tracking-wider uppercase mb-2">
                  {o.sub}
                </div>
                <div className="text-sm text-[#52525B] leading-relaxed">{o.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 simple steps */}
      <section className="border-t co-hairline bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <div className="co-label mb-3">How it works</div>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tighter leading-[1.1] max-w-3xl mb-12">
            Live in 4 simple steps. <span className="text-[#52525B]">Done — no manual follow-up.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { n: "01", t: "Import customers", d: "Bulk CSV upload or add one-by-one. Segment however you want." },
              { n: "02", t: "Set payment schedule", d: "Amount, cycle, due date. Attach your Razorpay or a payment portal." },
              { n: "03", t: "Reminders auto-fire", d: "7 / 3 / 0 days before due. WhatsApp, Email, SMS — you choose." },
              { n: "04", t: "Customer pays online", d: "Marked paid automatically via webhook. Next cycle rolls forward." },
            ].map((s) => (
              <div key={s.n} className="border co-hairline bg-white p-6">
                <div className="font-heading text-3xl font-bold text-[#4338CA] tracking-tight mb-3">{s.n}</div>
                <div className="font-heading text-base font-bold tracking-tight mb-2">{s.t}</div>
                <div className="text-sm text-[#52525B] leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why CollectOne — comparison */}
      <section className="border-t co-hairline">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <div className="co-label mb-3">Why CollectOne</div>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold tracking-tighter leading-[1.1] max-w-3xl mb-12">
            From chaos to <span className="text-[#4338CA]">clockwork</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 border co-hairline">
            <div className="p-8 border-b md:border-b-0 md:border-r co-hairline bg-[#FEF2F2]">
              <div className="text-[11px] font-bold text-[#B91C1C] tracking-wider uppercase mb-4">
                The old way
              </div>
              <ul className="space-y-3 text-sm text-[#7F1D1D]">
                {["Manual phone calls", "Excel spreadsheet tracking", "Following up on 5 apps", "Delayed payments", "Uncertain cash flow", "Commission-taking platforms"].map((x) => (
                  <li key={x} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#B91C1C]" /> {x}
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-8 bg-white">
              <div className="text-[11px] font-bold text-[#4338CA] tracking-wider uppercase mb-4">
                The CollectOne way
              </div>
              <ul className="space-y-3 text-sm text-[#0A0A0B]">
                {[
                  "Automatic reminders (WA · Email · SMS)",
                  "Live dashboard, always in sync",
                  "One place to manage everything",
                  "Payments land in 3 days average",
                  "Predictable, recurring revenue",
                  "Your own Razorpay, 0% commission",
                ].map((x) => (
                  <li key={x} className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[#4338CA] shrink-0" strokeWidth={2} /> {x}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Team access + multi-user */}
      <section className="border-t co-hairline bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14 grid lg:grid-cols-[auto_1fr] gap-10 items-center">
          <div className="w-16 h-16 bg-[#ECECFE] border border-[#C7D2FE] grid place-items-center rounded-sm shrink-0">
            <Users className="h-7 w-7 text-[#4338CA]" strokeWidth={1.75} />
          </div>
          <div>
            <div className="co-label mb-2">Multi-user · Team access</div>
            <h2 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight">
              Invite your team. Assign roles. Stay in control.
            </h2>
            <p className="mt-3 text-sm lg:text-base text-[#52525B] leading-relaxed max-w-2xl">
              Add owners, admins and staff to your workspace with a single click. Everyone gets
              the right level of access — from full-control owners to view-only staff. Your data
              stays 100% inside your workspace.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA — highlight 3-day trial */}
      <section className="border-t co-hairline bg-[#4338CA] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-20 text-center">
          <div
            data-testid="footer-cta-trial-badge"
            className="inline-flex items-center gap-1.5 bg-white/15 border border-white/20 px-3 py-1 rounded-sm text-[11px] font-bold tracking-wide uppercase mb-6"
          >
            <Sparkles className="h-3 w-3" strokeWidth={2.25} />
            3-day free trial · no card required
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter leading-[1.05] max-w-3xl mx-auto">
            Ready to stop chasing payments?
          </h2>
          <p className="mt-5 text-sm lg:text-base text-white/80 max-w-xl mx-auto leading-relaxed">
            Create your workspace in 30 seconds. Full access for 3 days. If it clicks, keep
            going — if not, walk away with zero commitment.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link
              to="/signup"
              data-testid="footer-signup-button"
              className="inline-flex items-center gap-2 bg-white text-[#4338CA] px-6 py-3 text-sm font-bold rounded-sm co-btn-press hover:bg-[#F4F4F5]"
            >
              Start 3-day free trial
              <ArrowRight className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 bg-transparent border border-white/40 text-white px-6 py-3 text-sm font-semibold rounded-sm co-btn-press hover:bg-white/10"
            >
              Sign in
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t co-hairline">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 text-xs text-[#52525B] flex justify-between">
          <div>© CollectOne 2026 — One platform for every collection.</div>
          <div>Built for India · INR · IST</div>
        </div>
      </footer>
    </div>
  );
}
